import { type FC, useEffect, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
import Checkmark from '@crehana/icons/outline/Checkmark';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import Edit2 from '@crehana/icons/outline/Edit2';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, IconButton, PrimaryButton, TextField } from '@crehana/ui';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { useForm } from 'react-hook-form';

import { useAttributes } from '@/views/AdminDashboard/components/AttributesDrawer/hooks';

import { MAX_LENGTH_ATTRIBUTE } from '../constants';
import DeleteDialog from '../DeleteDialog';

interface Tag {
  originalId: string | null;
  name: string;
}
interface TagListProps {
  attributeId: string;
  tag: Tag;
  showActions?: boolean;
  isDark?: boolean;
}

interface AttributeTagFrom {
  originalId: string;
  name: string;
}

const { baseLighter, grayMain, white } = colors;

const TagList: FC<React.PropsWithChildren<TagListProps>> = ({
  attributeId,
  tag,
  showActions,
  isDark,
}) => {
  const { t } = useTranslation();
  const { attributes, onEditTag, onClearTag, loadingTagPUT } = useAttributes();

  const [isEditing, setIsEditing] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<boolean>();

  const {
    handleSubmit,
    register,
    reset,
    watch,
    setError,
    errors,
    formState: { isValid, isDirty },
  } = useForm<AttributeTagFrom>({
    mode: 'onChange',
    defaultValues: {
      originalId: tag.originalId ? tag.originalId : '',
      name: tag.name,
    },
  });

  useEffect(() => {
    register('originalId');
  }, [register]);

  const onSubmit = async ({ originalId, name }: AttributeTagFrom) => {
    const attribute = attributes.find(attr => attr?.originalId === attributeId);
    const isDuplicated = attribute?.tags?.find(tag => tag?.name === name);

    if (isDuplicated) {
      setError('name', {
        type: 'duplicated',
        message: t('ATTR_LABEL_DUPLICATED'),
      });
    } else {
      await onEditTag({ id: originalId, name: name });
      onClose();
    }
  };

  const deleteTag = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (tag.originalId) onClearTag({ id: tag.originalId });
    setElementToDelete(false);
  };

  const onClose = () => {
    setIsEditing(false);
    reset();
  };

  const counterWatch = `${watch('name')?.length}/${MAX_LENGTH_ATTRIBUTE}`;

  const iconColor = isDark ? baseLighter : grayMain;
  const counterColor = `text-${isDark ? 'base-lighter' : 'base-light'}`;

  if (isEditing) {
    return (
      <form
        className="flex flex-row w-full mt-12 -mr-12 items-center"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField
          autoComplete="off"
          darkMode={isDark}
          name="name"
          hint={t('ATTR_INPUT_ADD')}
          maxLength={MAX_LENGTH_ATTRIBUTE}
          errorText={errors.name?.message}
          ref={register({
            required: {
              value: true,
              message: t('ATTR_INPUT_REQUIRED'),
            },
            maxLength: {
              value: MAX_LENGTH_ATTRIBUTE,
              message: t('ATTR_INPUT_MAX'),
            },
          })}
          css={`
            div {
              border-radius: 30px;
              max-height: 36px;
              input {
                max-height: 36px;
                min-height: 36px;
              }
            }
          `}
          endAdornment={
            <>
              <p className={`font-legals mr-8 ${counterColor}`}>
                {counterWatch}
              </p>
              <PrimaryButton
                type="submit"
                size="xs"
                className="pl-2 pr-2 mr-8 h-20 w-20"
                disabled={!isValid || !isDirty || loadingTagPUT}
                isDark={isDark}
              >
                <Checkmark
                  color={white}
                  size={16}
                  className="fill-current"
                  aria-hidden
                />
              </PrimaryButton>
              <IconButton
                className="pl-2 pr-2 h-20 w-20"
                icon={{ component: CloseSmall }}
                withoutBorders
                customSize={16}
                onClick={onClose}
                isDark={isDark}
                css={{ color: iconColor }}
              />
            </>
          }
        />
      </form>
    );
  }

  return (
    <>
      <div
        className="flex items-center justify-between py-8 px-12 border-solid border-gray-main mr-12 mt-12 max-h-36"
        style={{ borderRadius: '30px', maxHeight: '36px', borderWidth: 1 }}
      >
        <span className="font-body4 text-base-lighter">{tag.name || ''}</span>
        {showActions && (
          <div className="flex align-center">
            <IconButton
              className="h-16 w-16 ml-8 mr-8"
              icon={{ component: Edit2 }}
              withoutBorders
              customSize={16}
              css={{ color: iconColor }}
              isDark={isDark}
              onClick={() => setIsEditing(true)}
            />
            <IconButton
              className="h-16 w-16"
              icon={{ component: CloseSmall }}
              withoutBorders
              customSize={16}
              css={{ color: iconColor }}
              isDark={isDark}
              onClick={() => setElementToDelete(true)}
            />
          </div>
        )}
      </div>
      <DeleteDialog
        open={!!elementToDelete}
        onDismiss={() => setElementToDelete(false)}
        onConfirm={() => deleteTag()}
        isDark={isDark}
        title="ATTR_TAG_DELETE_DIALOG_TITLE"
        description="ATTR_TAG_DELETE_DIALOG_SUBTITLE"
        cancelButton="ATTR_TAG_DELETE_DIALOG_BUTTON_CANCEL"
        confirmButton="ATTR_TAG_DELETE_DIALOG_BUTTON_CONFIRM"
      />
    </>
  );
};

export default TagList;
